import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@connected-repo/ui-mui/feedback/Dialog";
import { Button } from "@connected-repo/ui-mui/form/Button";
import { TextField } from "@connected-repo/ui-mui/form/TextField";
import { Stack } from "@connected-repo/ui-mui/layout/Stack";
import { formatMinutes } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { toast } from "react-toastify";

interface AdjustMinutesDialogProps {
	open: boolean;
	onClose: () => void;
	teamId: string;
	teamName: string;
	balance: number;
}

/** Manual correction, either sign. A note is mandatory — it shows on the team's dashboard. */
export function AdjustMinutesDialog({ open, onClose, teamId, teamName, balance }: AdjustMinutesDialogProps) {
	const queryClient = useQueryClient();
	const [minutes, setMinutes] = useState("");
	const [note, setNote] = useState("");

	const mutation = useMutation(
		orpc.adjustMinutes.mutationOptions({
			onSuccess: async () => {
				await Promise.all([
					queryClient.invalidateQueries({ queryKey: orpc.getTeam.key() }),
					queryClient.invalidateQueries({ queryKey: orpc.listTeams.key() }),
				]);
				toast.success("Adjustment recorded");
				onClose();
			},
			onError: (err) => toast.error(err.message || "Failed to adjust minutes"),
		}),
	);

	const n = Number(minutes);
	const valid = Number.isInteger(n) && n !== 0 && note.trim().length >= 3;
	const after = Number.isInteger(n) ? balance + n : balance;

	const submit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!valid) return;
		mutation.mutate({ teamId, minutes: n, note: note.trim() });
	};

	return (
		<Dialog open={open} onClose={mutation.isPending ? undefined : onClose} fullWidth maxWidth="xs">
			<form onSubmit={submit} noValidate>
				<DialogTitle sx={{ fontWeight: 700 }}>Adjust minutes for {teamName}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<TextField
							autoFocus
							label="Minutes (negative to deduct)"
							type="number"
							value={minutes}
							onChange={(e) => setMinutes(e.target.value)}
							required
							fullWidth
							slotProps={{ htmlInput: { step: 1, inputMode: "numeric" } }}
							helperText={`Balance now ${formatMinutes(balance)} → after ${formatMinutes(after)}`}
						/>
						<TextField
							label="Reason"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							required
							fullWidth
							multiline
							minRows={2}
							helperText="Required. The team sees this on their dashboard."
						/>
						{after < 0 && (
							<Typography variant="body2" color="error">
								This takes the team's balance negative.
							</Typography>
						)}
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={onClose} color="inherit" disabled={mutation.isPending} sx={{ minHeight: 44 }}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" color={n < 0 ? "error" : "primary"} disabled={!valid || mutation.isPending} sx={{ minHeight: 44 }}>
						{mutation.isPending ? "Saving…" : "Record adjustment"}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
